import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import CurriculumPath from '../components/CurriculumPath';
import ChatPanel from '../components/ChatPanel';
import PinPanel from '../components/PinPanel';
import { fetchPrimaryChildCurriculumData } from '../lib/curriculum';
import {
  invokeEdgeFunction,
  minutesUntil
} from '../lib/parentSession';
import { supabase } from '../lib/supabaseClient';

const { FiCornerUpLeft, FiShield } = FiIcons;

function firstSelectableLessonId(curriculum) {
  for (const group of curriculum || []) {
    const lesson = group.lessons.find((item) => (
      item.status === 'available' || item.status === 'in-progress'
    ));
    if (lesson) return lesson.id;
  }
  return curriculum?.[0]?.lessons?.[0]?.id || null;
}

function StudentTutor() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [child, setChild] = useState(null);
  const [curriculum, setCurriculum] = useState([]);
  const [activeLessonId, setActiveLessonId] = useState(null);

  const [showParentPin, setShowParentPin] = useState(false);
  const [pin, setPin] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [pinError, setPinError] = useState('');

  const activeLesson = useMemo(() => {
    for (const group of curriculum) {
      const found = group.lessons.find(
        (lesson) => lesson.id === activeLessonId
      );

      if (found) {
        return {
          ...found,
          subject: group.subject
        };
      }
    }

    return null;
  }, [activeLessonId, curriculum]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchPrimaryChildCurriculumData();
        if (cancelled) return;

        setChild(data.child);
        setCurriculum(data.curriculum);
        setActiveLessonId((current) => (
          current || firstSelectableLessonId(data.curriculum)
        ));
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message || 'Could not load tutor data.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const refreshCurriculum = async () => {
    if (!child?.id) return;
    const data = await fetchPrimaryChildCurriculumData();
    setChild(data.child);
    setCurriculum(data.curriculum);
    setActiveLessonId((current) => {
      if (current && data.curriculum.some((group) => (
        group.lessons.some((lesson) => (
          lesson.id === current
          && (lesson.status === 'available' || lesson.status === 'in-progress')
        ))
      ))) {
        return current;
      }
      return firstSelectableLessonId(data.curriculum);
    });
  };

  const handleSelectLesson = (lesson) => {
    const isSelectable = (
      lesson.status === 'available' || lesson.status === 'in-progress'
    );
    if (!isSelectable) return;
    setActiveLessonId(lesson.id);
  };

  const handleCompleteLesson = async (lessonId) => {
    if (!child?.id) return;

    const { error: completeError } = await supabase.rpc(
      'complete_lesson_and_unlock_next',
      {
        p_child_id: child.id,
        p_lesson_id: lessonId
      }
    );

    if (completeError) {
      throw new Error('Could not mark lesson complete. Please try again.');
    }

    await refreshCurriculum();
  };

  const closeParentPin = () => {
    setShowParentPin(false);
    setPin('');
    setPinError('');
    setPinBusy(false);
  };

  const handleSwitchToParent = async (event) => {
    event.preventDefault();
    if (pin.length !== 4) return;

    setPinBusy(true);
    setPinError('');

    const { data, error: invokeError } = await invokeEdgeFunction(
      'verify-household-pin',
      { attempt_pin: pin }
    );

    if (data?.success === true && data.access_token && data.refresh_token) {
      try {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token
        });
        if (sessionError) throw sessionError;
        navigate('/parent', { replace: true });
        return;
      } catch (caught) {
        setPinBusy(false);
        setPinError(caught.message || 'Could not switch to parent session.');
        return;
      }
    }

    setPinBusy(false);

    if (data?.error === 'incorrect_pin') {
      const remaining = data.attempts_remaining;
      setPinError(
        typeof remaining === 'number'
          ? `Incorrect PIN. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
          : 'Incorrect PIN. Try again.'
      );
      setPin('');
      return;
    }

    if (data?.locked || data?.error === 'pin_locked') {
      const mins = minutesUntil(data.locked_until);
      setPinError(
        mins
          ? `Too many attempts. Try again in about ${mins} minute${mins === 1 ? '' : 's'}.`
          : 'Too many attempts. Try again later.'
      );
      setPin('');
      return;
    }

    if (data?.error === 'no_pin_set') {
      setPinError(
        'No parent PIN is set yet. Ask a parent to set one from the Parent Dashboard.'
      );
      setPin('');
      return;
    }

    setPinError(data?.error || invokeError || 'Could not switch to parent.');
  };

  if (loading) {
    return (
      <main className="student-page page-shell">
        <div className="dashboard-loading">
          <p>Loading tutor session…</p>
        </div>
      </main>
    );
  }

  if (error || !child) {
    return (
      <main className="student-page page-shell">
        <div className="dashboard-loading">
          <p className="dashboard-error">{error || 'No child data available.'}</p>
        </div>
      </main>
    );
  }

  return (
    <motion.main
      animate={{ opacity: 1 }}
      className="student-page page-shell"
      initial={{ opacity: 0 }}
    >
      <div className="student-welcome">
        <div>
          <span className="eyebrow">
            Good afternoon, {child.name}
          </span>
          <h1>Let’s make one tricky thing feel simple.</h1>
        </div>
        <div className="student-welcome-actions">
          <button
            className="pin-btn pin-btn-ghost"
            onClick={() => {
              setShowParentPin(true);
              setPin('');
              setPinError('');
            }}
            type="button"
          >
            <SafeIcon icon={FiCornerUpLeft} />
            Switch to Parent
          </button>
          <span className="practice-badge">
            <strong>3</strong>
            day learning streak
          </span>
        </div>
      </div>

      {showParentPin ? (
        <PinPanel
          busy={pinBusy}
          error={pinError}
          onCancel={closeParentPin}
          onPinChange={setPin}
          onSubmit={handleSwitchToParent}
          pin={pin}
          submitLabel="Enter parent mode"
          subtitle="Enter the parent PIN to leave the tutor and open the Parent Dashboard."
          title={(
            <>
              <SafeIcon icon={FiShield} />
              {' '}
              Switch to Parent
            </>
          )}
        />
      ) : null}

      <div className="tutor-layout">
        <CurriculumPath
          activeLessonId={activeLessonId}
          onSelectLesson={handleSelectLesson}
          subjects={curriculum}
        />
        <ChatPanel
          child={child}
          lesson={activeLesson}
          onCompleteLesson={handleCompleteLesson}
          onLessonStatusChanged={refreshCurriculum}
        />
      </div>
    </motion.main>
  );
}

export default StudentTutor;
